import { DocumentRetrievalExclusionReason } from "@common/types/task";
import { MonthStatus } from "./StatusCellContent";
import {
    MonthSeed,
    generateMonthlySeeds,
    getMonthKeyFromDate,
    getCoveredMonthKeys,
} from "./documentRetrievalDates";
import {
    getDocVerificationStatus,
    toExpectedCategorySet,
    DocForVerification,
    DocVerificationStatus,
} from "./documentVerificationStatus";

export interface MonthRow {
    key: string;
    label: string;
    fileName: string | null;
    documentId: number | null;
    status: MonthStatus;
    exclusionReason: DocumentRetrievalExclusionReason | null;
}

export interface UnverifiedDoc {
    id: number;
    fileName: string;
    period: string | null;
    verification: DocVerificationStatus;
}

export { generateMonthlySeeds };

export interface DocWithTaxonomy extends DocForVerification {
    fileName: string;
}

export function matchDocumentsToMonths({
    months,
    docs,
    excludedPeriods,
    expectedCategoryIds,
    expectedAccountNumberCandidates,
    expectedAccountNumberCandidatesLoading,
    now,
}: {
    months: MonthSeed[];
    docs: DocWithTaxonomy[];
    excludedPeriods: Record<string, DocumentRetrievalExclusionReason>;
    expectedCategoryIds?: string[];
    expectedAccountNumberCandidates?: readonly string[];
    expectedAccountNumberCandidatesLoading?: boolean;
    now?: Date;
}): { rows: MonthRow[]; unverifiedDocs: UnverifiedDoc[] } {
    const monthKeys = new Set(months.map((m) => m.key));
    const expectedCategorySet = toExpectedCategorySet(expectedCategoryIds);
    const occupied = new Map<string, DocWithTaxonomy>();
    const occupiedMonths = new Map<string, number>();

    const coveredKeysCache = new Map<number, string[]>();
    const cachedGetCoveredMonthKeys = (doc: DocForVerification): string[] => {
        const cached = coveredKeysCache.get(doc.id);
        if (cached) {
            return cached;
        }
        const keys = getCoveredMonthKeys(doc);
        coveredKeysCache.set(doc.id, keys);
        return keys;
    };

    const orderedDocs = [...docs].sort((a, b) => a.id - b.id);
    for (const doc of orderedDocs) {
        const coveredKeys = cachedGetCoveredMonthKeys(doc).filter(
            (k) => monthKeys.has(k) && !occupied.has(k),
        );
        if (coveredKeys.length === 0) {
            continue;
        }
        const verification = getDocVerificationStatus(doc, {
            monthKeys,
            occupiedMonths,
            expectedCategoryIds: expectedCategorySet,
            expectedAccountNumberCandidates,
            expectedAccountNumberCandidatesLoading,
            now,
            getCoveredMonthKeysFn: cachedGetCoveredMonthKeys,
        });
        if (verification.invalidFields.size === 0) {
            for (const key of coveredKeys) {
                occupied.set(key, doc);
                occupiedMonths.set(key, doc.id);
            }
        }
    }

    const occupyingIds = new Set(occupiedMonths.values());
    const unverified: UnverifiedDoc[] = [];
    const unverifiedIds = new Set<number>();
    for (const doc of docs) {
        if (occupyingIds.has(doc.id)) {
            continue;
        }
        const verification = getDocVerificationStatus(doc, {
            monthKeys,
            occupiedMonths,
            expectedCategoryIds: expectedCategorySet,
            expectedAccountNumberCandidates,
            expectedAccountNumberCandidatesLoading,
            now,
            getCoveredMonthKeysFn: cachedGetCoveredMonthKeys,
        });
        const endDate = doc.taxonomyExtraction?.periodEndDate;
        const period = endDate ? getMonthKeyFromDate(endDate) : null;
        unverified.push({
            id: doc.id,
            fileName: doc.fileName,
            period,
            verification,
        });
        unverifiedIds.add(doc.id);
    }

    const rows: MonthRow[] = months.map((month) => {
        const matched = occupied.get(month.key);
        const exclusionReason = excludedPeriods[month.key] ?? null;

        let status: MonthStatus;
        const matchedIsVerified = !!matched && !unverifiedIds.has(matched.id);
        if (matchedIsVerified) {
            status = "verified";
        } else if (exclusionReason) {
            status = "excluded";
        } else {
            status = "awaiting";
        }

        return {
            ...month,
            fileName: matchedIsVerified ? (matched?.fileName ?? null) : null,
            documentId: matchedIsVerified ? (matched?.id ?? null) : null,
            status,
            exclusionReason,
        };
    });

    return { rows, unverifiedDocs: unverified };
}
